import type { FC } from "react";

import { isNil } from "lodash";

import { formatDate } from "@similarweb/pro-logic-components";
import { PlainTooltip } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { simplePercentageFilter } from "app/filters/ngFilters";
import { LoadingSpinner } from "app/pages/keyword-analysis/TotalTrafficPage/StyledComponents";
import { getTotalScoreColor } from "./utils";

import NoDataDemandSize from "../demand-size/NoDataDemandSize";
import {
    ChangeWrapper,
    DataText,
    LoaderContainerBackground,
    NoDataContainerBackground,
    VolumeChangeDates,
    VolumeChangeLabel,
    VolumeChangeNumber,
} from "../demand-size/StyledComponent";
import type { IDemandSizeChange, ITotalScoreColors } from "../demand-size/types";

const PLUS_SIGN = "+";
const DemandSizeChange: FC<IDemandSizeChange> = ({
    date,
    isLoading,
    rawData,
    customLabel,
    totalScore: totalScoreProp = null,
    customChangeLabel,
    labels,
    className,
    loaderClassName,
    tooltip = "demand.size.change.widget.tooltip",
    isPP = false,
    loaderTopGap,
}) => {
    if (isLoading) {
        return (
            <LoaderContainerBackground className={loaderClassName}>
                <LoadingSpinner top={loaderTopGap} />
            </LoaderContainerBackground>
        );
    }

    const isNoData = !rawData || isNil(rawData?.Volume?.Change);
    if (isNoData) {
        return (
            <NoDataContainerBackground className={className}>
                <NoDataDemandSize
                    noDataTitleKey="apps.common.na"
                    noDataSubTitleKey="demand.data.change.no.data"
                />
            </NoDataContainerBackground>
        );
    }
    const getTotalScore = () => {
        const totalScore = totalScoreProp ?? rawData?.TotalScore;
        const totalScoreLabel = labels ? labels[totalScore] : totalScore;
        return { totalScoreLabel, totalScore };
    };

    const { totalScore, totalScoreLabel } = getTotalScore();
    const change = rawData?.Volume?.Change;
    const TotalScoreColor: ITotalScoreColors = getTotalScoreColor(totalScore);
    const formattedDate = date && formatDate(date[0]?.value?.from, date[0]?.value?.to);
    const label = customLabel ?? formattedDate;
    const valueSymbol = isPP ? " pp" : "%";

    return (
        <ChangeWrapper
            color={TotalScoreColor}
            className={className}
        >
            <PlainTooltip tooltipContent={translate(tooltip)}>
                <VolumeChangeNumber>
                    <DataText color={TotalScoreColor}>
                        {change > 0 ? PLUS_SIGN : null}
                        {simplePercentageFilter()(change, 2)}
                        {valueSymbol}
                        {customChangeLabel ? ` ${customChangeLabel}` : null}
                    </DataText>
                </VolumeChangeNumber>
            </PlainTooltip>
            <VolumeChangeLabel>
                <DataText color={TotalScoreColor}>{totalScoreLabel}</DataText>
            </VolumeChangeLabel>
            {label ? <VolumeChangeDates>{label}</VolumeChangeDates> : null}
        </ChangeWrapper>
    );
};

export default DemandSizeChange;
